"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.anthropicProvider = void 0;
/* eslint-disable no-nested-ternary */
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const media_1 = require("../../../core/normalize/message-parts/media");
const reasoning_1 = require("../../../core/normalize/message-parts/reasoning");
const tool_calls_1 = require("../../../core/normalize/message-parts/tool-calls");
const tool_results_1 = require("../../../core/normalize/message-parts/tool-results");
const tool_definitions_1 = require("../../../core/normalize/tool-definitions");
/**
 * Anthropic Messages API convention: this module owns Anthropic's typed
 * block vocabulary and its `system` request field. One message can still
 * contain parts from several dialects — the core folds every provider
 * cumulatively.
 */
// Anthropic `stop_reason` vocabulary -> the canonical FinishReason set.
const ANTHROPIC_FINISH_REASON_TYPE_BY_RAW = {
    end_turn: "stop",
    stop_sequence: "stop",
    max_tokens: "length",
    tool_use: "tool-calls",
    refusal: "content-filter",
    pause_turn: "other",
};
const ANTHROPIC_TOOL_RESULT_PART_TYPES = [
    "tool_result",
    "web_search_tool_result",
    "code_execution_tool_result",
    "bash_code_execution_result",
    "text_editor_code_execution_tool_result",
    "text_editor_code_execution_view_result",
    "mcp_tool_result",
];
/** Anthropic `source` objects: `{type: "base64" | "url" | "file", ...}`. */
function filePartFromAnthropicSource(source, options = {}) {
    const mediaType = (0, json_1.optionalString)(source.media_type);
    const url = (0, json_1.optionalString)(source.url);
    if (source.type === "url" && url) {
        return (0, media_1.filePartFromUrl)(url, { ...options, mediaType });
    }
    const data = (0, json_1.optionalString)(source.data);
    const fileId = (0, json_1.optionalString)(source.file_id);
    const content = source.type === "base64" && data
        ? { kind: "base64", data }
        : source.type === "file" && fileId
            ? { kind: "reference", id: fileId }
            : undefined;
    if (!content)
        return null;
    return (0, json_1.compact)({
        type: "file",
        mediaType: mediaType ?? options.fallbackMediaType,
        content,
        providerMetadata: options.extras
            ? (0, json_1.toProviderMetadata)(options.extras)
            : undefined,
    });
}
const normalizeAnthropicImage = (value) => {
    // Anthropic image blocks carry a `source`; source-less `image` parts on
    // the same type name belong to the AI SDK dialect and fall through.
    const source = (0, json_1.asRecord)(value.source);
    if (!source)
        return __1.unmatched;
    const part = filePartFromAnthropicSource(source, {
        fallbackMediaType: "image/*",
    });
    return part ? (0, __1.claimed)(part) : __1.unmatched;
};
// Anthropic call blocks: { type, id, name, input }. Streamed emissions
// also carry the content-block `index` — it stays in providerMetadata.
const anthropicToolCall = (value, toolType) => (0, tool_calls_1.toolCallPart)({
    toolCallId: value.id,
    toolName: value.name,
    input: value.input,
    toolType,
});
const normalizeAnthropicServerToolCall = (value) => (0, __1.claimed)((0, tool_calls_1.providerExecutedToolCall)(anthropicToolCall(value, "server_tool_use")));
const normalizeAnthropicMcpToolCall = (value) => (0, __1.claimed)((0, tool_calls_1.providerExecutedToolCall)(anthropicToolCall(value, "mcp_tool_use"), (0, json_1.compact)({ server_name: (0, json_1.optionalString)(value.server_name) })));
const normalizeAnthropicThinking = (value) => {
    if (value.thinking === null || value.thinking === undefined)
        return __1.unmatched;
    return (0, __1.claimed)((0, reasoning_1.reasoningPart)(value.thinking, (0, json_1.optionalString)(value.signature)));
};
const normalizeAnthropicRedactedThinking = (value) => {
    const data = (0, json_1.optionalString)(value.data);
    return data
        ? (0, __1.claimed)({ type: "reasoning", content: { kind: "redacted", data } })
        : (0, __1.claimed)((0, reasoning_1.reasoningPart)(value.data ?? null));
};
const normalizeAnthropicDocument = (value) => {
    const source = (0, json_1.asRecord)(value.source);
    const part = source
        ? filePartFromAnthropicSource(source, {
            extras: (0, json_1.compact)({
                title: (0, json_1.optionalString)(value.title),
                context: (0, json_1.optionalString)(value.context),
                citations: Array.isArray(value.citations)
                    ? value.citations
                    : undefined,
            }),
        })
        : null;
    if (part)
        return (0, __1.claimed)(part);
    // Text and structured-content documents are semantic content, but do not
    // have a file reference that a renderer can resolve.
    return (0, __1.claimed)({
        type: "custom",
        kind: "document",
        value: (0, json_1.toJsonValue)(value),
    });
};
const normalizeAnthropicContainerUpload = (value) => {
    const fileId = (0, json_1.optionalString)(value.file_id);
    if (!fileId)
        return __1.unmatched;
    // Opaque reference: no media-type signal (README assumption 11).
    return (0, __1.claimed)({ type: "file", content: { kind: "reference", id: fileId } });
};
// Anthropic result blocks: { type: "tool_result" | "*_tool_result",
// tool_use_id, content, is_error? }.
const normalizeAnthropicToolResult = (value, context) => {
    const raw = (0, json_1.parseIfString)(value.content ?? null);
    let output = typeof raw === "string"
        ? { type: "text", value: raw }
        : { type: "json", value: (0, json_1.toJsonValue)(raw) };
    if (Array.isArray(value.content)) {
        output = (0, tool_results_1.toolResultOutput)(context.normalizePartList(value.content), raw);
    }
    return (0, __1.claimed)((0, json_1.compact)({
        type: "tool-result",
        toolCallId: (0, json_1.nullableString)(value.tool_use_id ?? value.id),
        output,
        isError: typeof value.is_error === "boolean" ? value.is_error : undefined,
    }));
};
const ANTHROPIC_PART_HANDLERS = {
    tool_use: (value) => (0, __1.claimed)(anthropicToolCall(value, "tool_use")),
    image: normalizeAnthropicImage,
    server_tool_use: normalizeAnthropicServerToolCall,
    mcp_tool_use: normalizeAnthropicMcpToolCall,
    thinking: normalizeAnthropicThinking,
    redacted_thinking: normalizeAnthropicRedactedThinking,
    document: normalizeAnthropicDocument,
    container_upload: normalizeAnthropicContainerUpload,
    ...Object.fromEntries(ANTHROPIC_TOOL_RESULT_PART_TYPES.map((type) => [
        type,
        normalizeAnthropicToolResult,
    ])),
};
/**
 * Anthropic's `system` request field: a string or content-block array,
 * carried beside `content` rather than in the message stream. Wrapped as
 * message content and forced to the system role.
 */
function anthropicSystemMessage(root, kind) {
    if (kind !== "input" || !("system" in root))
        return undefined;
    return {
        kind: "single",
        value: { content: (0, json_1.recordKeyAsParsed)(root, "system") },
        fallbackRole: "user",
        roleOverride: "system",
    };
}
// Anthropic carries citations under `citations` on text parts and document
// sources.
const ANTHROPIC_CITATION_KEYS = new Set(["citations"]);
exports.anthropicProvider = {
    name: "anthropic",
    finishReasonTypeByRaw: ANTHROPIC_FINISH_REASON_TYPE_BY_RAW,
    typedParts: ANTHROPIC_PART_HANDLERS,
    citationKeys: ANTHROPIC_CITATION_KEYS,
    // Anthropic tool declarations: { name, description, input_schema }.
    tryNormalizeToolDefinition: (value) => {
        if (value.input_schema === undefined)
            return __1.unmatched;
        const definition = (0, tool_definitions_1.toolDefinition)({
            name: value.name,
            description: value.description,
            inputSchema: value.input_schema,
            type: value.type,
            providerMetadata: (0, tool_definitions_1.toolDefinitionProviderMetadata)(value, value),
        });
        return definition ? (0, __1.claimed)(definition) : __1.unmatched;
    },
    getSystemMessage: anthropicSystemMessage,
};
